import { SupportedMimeType, RecordingType } from '../types/media.enums';
import { getBrowserInfo } from '../utils';

// MIME type priorities
export const MIME_TYPE_PRIORITIES: SupportedMimeType[] = [
  // iOS-friendly formats first
  SupportedMimeType.VIDEO_MP4_H264,
  SupportedMimeType.AUDIO_MP4,
  SupportedMimeType.VIDEO_MP4,

  // WebM formats for modern browsers
  SupportedMimeType.VIDEO_WEBM_VP9,
  SupportedMimeType.VIDEO_WEBM_VP8,
  SupportedMimeType.VIDEO_WEBM,
  SupportedMimeType.AUDIO_WEBM_OPUS,
  SupportedMimeType.AUDIO_WEBM,

  // Fallbacks
  SupportedMimeType.AUDIO_MP3,
  SupportedMimeType.AUDIO_WAV,
];

// Get best supported MIME type for browser
export function getBestSupportedMimeType(
  type: RecordingType = RecordingType.VIDEO,
): SupportedMimeType | null {
  if (!isMediaRecorderSupported()) return null;

  const { name: browserName } = getBrowserInfo();
  const isFirefox = browserName === 'Firefox';

  // Firefox requires 'video/webm' for audio+video recording
  if (
    isFirefox &&
    type === RecordingType.VIDEO &&
    MediaRecorder.isTypeSupported(SupportedMimeType.VIDEO_WEBM)
  ) {
    return SupportedMimeType.VIDEO_WEBM;
  }

  const relevantTypes = MIME_TYPE_PRIORITIES.filter((mimeType) =>
    type === RecordingType.AUDIO ? mimeType.includes('audio/') : mimeType.includes('video/'),
  );

  for (const mimeType of relevantTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }

  // Fallback to basic types
  return type === RecordingType.AUDIO ? SupportedMimeType.AUDIO_MP3 : SupportedMimeType.VIDEO_WEBM;
}

// Check if MediaRecorder is supported
export function isMediaRecorderSupported(): boolean {
  return !!(typeof window !== 'undefined' && window.MediaRecorder);
}

// Get all supported MIME types for current browser
export function getSupportedMimeTypes(type?: RecordingType): SupportedMimeType[] {
  if (!isMediaRecorderSupported()) return [];

  const typesToCheck = type
    ? MIME_TYPE_PRIORITIES.filter((mimeType) =>
        type === RecordingType.AUDIO ? mimeType.includes('audio/') : mimeType.includes('video/'),
      )
    : MIME_TYPE_PRIORITIES;

  return typesToCheck.filter((mimeType) => MediaRecorder.isTypeSupported(mimeType));
}
